import type { FC, ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { logger } from '@/shared/utils/logger';

export interface NetworkStatusContextValue {
  isOnline: boolean;
  wasOffline: boolean;
}

const NetworkStatusContext = createContext<
  NetworkStatusContextValue | undefined
>(undefined);

export const useNetworkStatusContext = () => {
  const context = useContext(NetworkStatusContext);
  if (!context) {
    throw new Error(
      'useNetworkStatusContext must be used within NetworkStatusProvider',
    );
  }
  return context;
};

export const useOptionalNetworkStatusContext = () =>
  useContext(NetworkStatusContext);

interface NetworkStatusProviderProps {
  children: ReactNode;
}

/**
 * Global network status provider
 * Monitors navigator.onLine and provides status to entire app
 */
export const NetworkStatusProvider: FC<NetworkStatusProviderProps> = ({
  children,
}) => {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [wasOffline, setWasOffline] = useState<boolean>(false);

  useEffect(() => {
    let resetTimeout: ReturnType<typeof setTimeout> | undefined;

    const handleOnline = () => {
      logger.info('Network: Back online');
      setIsOnline(true);
      setWasOffline(true);

      // Reset wasOffline after brief delay (for toast display)
      resetTimeout = setTimeout(() => {
        setWasOffline(false);
      }, 4000);
    };

    const handleOffline = () => {
      logger.info('Network: Gone offline');
      setIsOnline(false);
      setWasOffline(false);
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      if (resetTimeout) {
        clearTimeout(resetTimeout);
      }
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const value = useMemo(
    () => ({ isOnline, wasOffline }),
    [isOnline, wasOffline],
  );

  return (
    <NetworkStatusContext.Provider value={value}>
      {children}
    </NetworkStatusContext.Provider>
  );
};
